import {
  IStudyProtocol,
  IStudyPersonnelProtocol,
  IAddEditStudySiteProtocol
} from "../../../../model";
import { sortObjectArray } from "../../../../utils/functionsHelper";

/**
 * Get the display name for a Primary Investigator
 */
export const getPIName = (primaryInvestigator?: {
  firstName?: string;
  lastName?: string;
}): string => {
  if (!primaryInvestigator) {
    return "";
  }

  const firstName = primaryInvestigator.firstName || "N/A";
  const lastName = primaryInvestigator.lastName || "";

  return `${firstName} ${lastName}`.trim();
};

/**
 * Sort protocols by name
 */
export const sortProtocols = (
  protocols: IStudyProtocol[]
): IStudyProtocol[] => {
  return sortObjectArray(protocols, "ProtocolName");
};

/**
 * Sort protocols for edit mode - new protocols at top, existing at bottom
 * Both groups are sorted alphabetically by name
 */
export const sortProtocolsForEditMode = (
  protocols: IAddEditStudySiteProtocol[]
): IAddEditStudySiteProtocol[] => {
  const newProtocols = protocols
    .filter(p => !p.isExisting)
    .sort((a, b) => a.protocolName.localeCompare(b.protocolName));

  const existingProtocols = protocols
    .filter(p => p.isExisting)
    .sort((a, b) => a.protocolName.localeCompare(b.protocolName));

  return [...newProtocols, ...existingProtocols];
};

/**
 * Create protocol objects for study site
 */
export const createProtocolObjects = (protocols: IStudyProtocol[]) => {
  return protocols.map(prot => ({
    id: prot.ProtocolId,
    protocolName: prot.ProtocolName
  }));
};

/**
 * Update sponsor site IDs for all protocols
 * @param protocols - Array of protocols to update
 * @param sponsorSiteId - The sponsor site ID to apply (can be empty string to clear)
 * @param forceOverwrite - If true, overwrite all protocol IDs. If false, only fill empty ones.
 * @returns Updated protocols array with sponsor site IDs applied
 */
export const updateProtocolSponsorIds = (
  protocols: IStudyPersonnelProtocol[],
  sponsorSiteId: string,
  forceOverwrite: boolean
): IStudyPersonnelProtocol[] => {
  return protocols.map(protocol => ({
    ...protocol,
    sponsorSiteId: forceOverwrite
      ? sponsorSiteId
      : protocol.sponsorSiteId || sponsorSiteId
  }));
};

/**
 * Update a single protocol in a study site's protocols array
 * @param studySite - The study site containing the protocols array
 * @param protocolId - ID of the protocol to update
 * @param updates - Partial protocol object with fields to update
 * @returns Updated study site with the protocol modified
 */
export const updateProtocolInStudySite = <
  T extends { protocols?: IAddEditStudySiteProtocol[] }
>(
  studySite: T,
  protocolId: string,
  updates: Partial<IAddEditStudySiteProtocol>
): T => {
  const updatedProtocols = (
    studySite.protocols || []
  ).map((p: IAddEditStudySiteProtocol) =>
    p.id === protocolId ? { ...p, ...updates } : p
  );

  return {
    ...studySite,
    protocols: updatedProtocols
  };
};

/**
 * Get breadcrumb configuration for study site operations
 * @param studyName - Name of the study
 * @param studyId - ID of the study
 * @param mode - Either 'add' or 'edit'
 * @param siteName - Name of the site (required for edit mode)
 */
export const getBreadcrumbConfig = (
  studyName: string,
  studyId: string,
  mode: "add" | "edit",
  siteName?: string
) => {
  const currentNestedLocation =
    mode === "add" ? "Add New Association" : `${siteName} Association`;

  return {
    rootText: "All Studies",
    rootUrl: "/study",
    currentLocation: studyName,
    currentNestedLocation,
    currentNestedLocationUrl: `/study/${studyId}/sites`
  };
};
